import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Store, Search, Plus, MoreHorizontal, Eye, Trash2, Globe, GlobeLock } from 'lucide-react';
import { adminApi, resolveUrl } from '@/services/adminApi';
import { queryKeys } from '@/lib/queryKeys';
import {
  PageHeader,
  PageContent,
  Tabs,
  EmptyState,
  LoadingState,
  Button,
  Table,
  AlertDialog,
  StatusBadge,
} from '@printtat/ui';
import type { Column } from '@printtat/ui';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@printtat/ui';
import type { CatalogProductListItem } from '@printtat/shared';
import { formatPrice } from '@printtat/shared';

const TAB_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'draft', label: 'Draft' },
];

export const AdminCatalogPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.catalog.all(), { search, tab, page, limit }],
    queryFn: () => adminApi.catalog.list({
      search: search || undefined,
      page,
      limit,
    }),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => adminApi.catalog.publish(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all() }),
  });

  const unpublishMutation = useMutation({
    mutationFn: (id: string) => adminApi.catalog.unpublish(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all() }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.catalog.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all() });
      setDeletingId(null);
    },
  });

  const allItems = data?.items ?? [];
  const filteredItems = tab === 'all'
    ? allItems
    : tab === 'published'
      ? allItems.filter((i) => i.isPublished)
      : allItems.filter((i) => !i.isPublished);

  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const columns: Column<CatalogProductListItem>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (item) => (
        <div className="flex items-center gap-3">
          {item.previewUrl ? (
            <img
              src={resolveUrl(item.previewUrl)}
              alt=""
              crossOrigin="anonymous"
              className="w-10 h-10 rounded-lg object-cover bg-gray-100"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <Store size={16} className="text-gray-400" />
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-gray-900">{item.displayName}</p>
            <p className="text-xs text-gray-500">{item.productName} &middot; /{item.slug}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (item) => (
        <span className="text-sm text-gray-600 capitalize">{item.category}</span>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      render: (item) => (
        <span className="text-sm text-gray-700">{formatPrice(item.price)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusBadge
          label={item.isPublished ? 'Published' : 'Draft'}
          color={item.isPublished ? 'green' : 'gray'}
          size="xs"
        />
      ),
    },
    {
      key: 'sortOrder',
      header: 'Order',
      render: (item) => (
        <span className="text-sm text-gray-500">{item.sortOrder}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      className: 'w-10',
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal size={16} className="text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/catalog/${item.id}`)}>
              <Eye size={14} className="mr-2" />
              View details
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                if (item.isPublished) {
                  unpublishMutation.mutate(item.id);
                } else {
                  publishMutation.mutate(item.id);
                }
              }}
            >
              {item.isPublished ? (
                <><GlobeLock size={14} className="mr-2" />Unpublish</>
              ) : (
                <><Globe size={14} className="mr-2" />Publish</>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={() => setDeletingId(item.id)}
            >
              <Trash2 size={14} className="mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PageContent className="overflow-y-auto h-screen">
      <PageHeader
        icon={Store}
        title="Catalog"
        description={`${total} catalog product${total !== 1 ? 's' : ''}`}
        actions={
          <Button onClick={() => navigate('/catalog/new')}>
            <Plus size={16} className="mr-1.5" />
            Add product
          </Button>
        }
      />

      <div className="mt-6 flex items-center gap-3">
        <Tabs
          tabs={TAB_OPTIONS}
          value={tab}
          onChange={(v) => { setTab(v); setPage(1); }}
          variant="pill"
        />
        <div className="relative flex-1 max-w-sm ml-auto">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search catalog..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <LoadingState variant="skeleton" columns={1} rows={5} />
        ) : filteredItems.length === 0 ? (
          <EmptyState
            icon={Store}
            title="No catalog products"
            description={search || tab !== 'all' ? 'Try adjusting your filters' : 'Add your first catalog product'}
          />
        ) : (
          <>
            <Table
              columns={columns}
              data={filteredItems}
              keyExtractor={(d) => d.id}
              onRowClick={(d) => navigate(`/catalog/${d.id}`)}
            />

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <AlertDialog
        open={!!deletingId}
        onOpenChange={(open) => { if (!open) setDeletingId(null); }}
        title="Delete catalog product"
        description="This will permanently remove this product from the catalog. The underlying design will not be affected."
        confirmText="Delete"
        onConfirm={() => { if (deletingId) deleteMutation.mutate(deletingId); }}
        variant="destructive"
      />
    </PageContent>
  );
};
